import { useCallback } from 'react';

import { checkHasPermission } from 'src/permissions/permission-engine';

import {
  getUserRole,
  getTenantName,
  type PermissionsMap,
} from './permission-utils';

type Params = {
  permissions: PermissionsMap | null;
  loginApiData: unknown;
};

export function useNavPermissionFilter({ permissions, loginApiData }: Params) {
  return useCallback(
    (navList: any[]) => {
      if (!permissions) return navList;

      const tenantName = getTenantName(loginApiData);
      const role = getUserRole(loginApiData);

      const checkPermission = (id: string | string[]): boolean =>
        checkHasPermission(permissions, role, id);

      return navList.map((group) => {
        const filteredItems = (group.items || [])
          .map((item: any) => {
            const key = typeof item.id === 'string' ? item.id.toLowerCase() : item.id;

            if (key === 'setting' || (Array.isArray(key) && key.includes('setting'))) {
              const visibleChildren = (item.children || []).flatMap((child: any) => {
                const childId = typeof child.id === 'string' ? child.id.toLowerCase() : child.id;

                if (
                  (childId === 'bill' ||
                    childId === 'billing' ||
                    (Array.isArray(childId) &&
                      (childId.includes('bill') || childId.includes('billing')))) &&
                  (role === 'Admin' || role?.toLowerCase() === 'management') &&
                  tenantName &&
                  tenantName !== 'MASTER'
                ) {
                  return checkPermission(childId) ? [child] : [];
                }

                // Access Control is dynamically governed by the 'role' permission.
                if (child?.title === 'ACCESS_CONTROL') {
                  return checkPermission(childId) ? [child] : [];
                }

                return checkPermission(childId) ? [child] : [];
              });

              return visibleChildren.length > 0 ? { ...item, children: visibleChildren } : null;
            }

            if (
              (key === 'report' || (Array.isArray(key) && key.includes('report'))) &&
              role === 'Admin'
            ) {
              const visibleChildren = (item.children || []).filter((child: any) => {
                const childId = typeof child.id === 'string' ? child.id.toLowerCase() : child.id;
                return checkPermission(childId);
              });
              return visibleChildren.length > 0 ? { ...item, children: visibleChildren } : null;
            }

            if (
              key === 'csat' ||
              (Array.isArray(key) && (key.includes('csat_analytics') || key.includes('csat_config')))
            ) {
              const visibleChildren = (item.children || []).filter((child: any) => {
                const childId = typeof child.id === 'string' ? child.id.toLowerCase() : child.id;
                return checkPermission(childId);
              });
              return visibleChildren.length > 0 ? { ...item, children: visibleChildren } : null;
            }

            if (item.children && Array.isArray(item.children)) {
              const visibleChildren = item.children.filter((child: any) => {
                const childId = typeof child.id === 'string' ? child.id.toLowerCase() : child.id;
                return checkPermission(childId);
              });
              return visibleChildren.length > 0 ? { ...item, children: visibleChildren } : null;
            }

            return checkPermission(key) && key !== 'report' ? item : null;
          })
          .filter(Boolean);

        return { ...group, items: filteredItems };
      });
    },
    [permissions, loginApiData]
  );
}
